import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, doc, setDoc } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { Link, useNavigate } from 'react-router-dom';
import { ButtonGroup, Button, Badge, Container, Row, Col, Alert } from 'react-bootstrap';
import { getFlightStatus } from '../services/flightTracker';

interface Member {
  id?: string;
  Name: string;
  Email: string;
  Title?: string;
  Company?: string;
  photoURL?: string;
  'Flight # incoming'?: string;
  'Flight arrival time'?: string;
  'Flight Arrival Time'?: string;
  flightArrivalTime?: string;
  [key: string]: any; // For other dynamic fields
}

interface FlightStatus {
  status: string;
  [key: string]: any;
}

function Members() {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [flightStatuses, setFlightStatuses] = useState<Record<string, FlightStatus>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setApiError(null);

        // Check super user status first
        const currentUser = auth.currentUser;
        if (currentUser?.email === 'jared@heytheremedia.com') {
          try {
            const superUsersRef = collection(db, 'super_users');
            const q = query(superUsersRef, where('email', '==', currentUser.email));
            const querySnapshot = await getDocs(q);
            
            if (querySnapshot.empty) {
              // Create super user document if it doesn't exist
              const superUserRef = doc(collection(db, 'super_users'));
              await setDoc(superUserRef, {
                email: currentUser.email,
                super_user: true,
                created_at: new Date().toISOString()
              });
            }
            setIsSuperUser(true);
            setHasAccess(true); // Super users always have access
          } catch (error) {
            console.error('Error checking super user status:', error);
          }
        }

        // Fetch members
        const colRef = collection(db, 'members');
        const snapshot = await getDocs(colRef);
        const membersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Member[];
        
        // If not a super user, check if the current user has a member profile
        if (!isSuperUser && currentUser?.email) {
          const hasProfile = membersData.some(member => 
            member.Email?.toLowerCase() === currentUser.email?.toLowerCase()
          );
          setHasAccess(hasProfile);
        }

        setMembers(membersData);
        
        // Only fetch flight statuses if user has access
        if (hasAccess || isSuperUser) {
          const statuses: Record<string, FlightStatus> = {};
          for (const member of membersData) {
            const flightNumber = member['Flight # incoming'];
            if (flightNumber) {
              try {
                const status = await getFlightStatus(flightNumber);
                if (status) {
                  statuses[flightNumber] = status;
                }
              } catch (error) {
                console.error(`Error fetching status for flight ${flightNumber}:`, error);
              }
            }
          }
          setFlightStatuses(statuses);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setApiError('Error loading members. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isSuperUser, hasAccess]);

  const sortMembers = (field: string) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedMembers = () => {
    if (!sortField) return members;

    return [...members].sort((a, b) => {
      let aValue = a[sortField] || a.Name || '';
      let bValue = b[sortField] || b.Name || '';

      // Handle arrival time sorting
      if (sortField === 'Flight arrival time') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const getStatusBadge = (flightNumber: string) => {
    if (!flightNumber || !flightStatuses[flightNumber]) return null;
    
    const status = flightStatuses[flightNumber].status;
    let variant = 'secondary';
    
    switch (status?.toLowerCase()) {
      case 'scheduled':
        variant = 'info';
        break;
      case 'active':
      case 'en-route':
        variant = 'primary';
        break;
      case 'landed':
        variant = 'success';
        break;
      case 'delayed':
        variant = 'warning';
        break;
      case 'cancelled':
        variant = 'danger';
        break;
      default:
        variant = 'secondary';
    }
    
    return (
      <Badge bg={variant} style={{ marginLeft: '10px' }}>
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const canEditRecord = (memberEmail: string) => {
    return isSuperUser || (auth.currentUser?.email?.toLowerCase() === memberEmail?.toLowerCase());
  };

  if (loading) {
    return <div>Loading members...</div>;
  }

  // If user doesn't have access and isn't a super user, show access denied
  if (!hasAccess && !isSuperUser) {
    return (
      <Container style={{ maxWidth: '1200px', padding: '20px' }}>
        <Alert variant="warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          You do not have access to view member information. Please contact an administrator if you believe this is an error.
        </Alert>
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: '1200px', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ marginBottom: '0' }}>Shredder Directory</h2>
          {isSuperUser && (
            <Badge bg="warning" text="dark" className="me-3">Super User Mode</Badge>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {isSuperUser && (
            <Button
              variant="success"
              onClick={() => navigate('/members/new')}
              className="me-2"
            >
              <i className="bi bi-plus-circle me-2"></i>
              Create New Member
            </Button>
          )}
          <ButtonGroup>
            <Button
              variant={sortField === 'Name' ? 'primary' : 'outline-primary'}
              onClick={() => sortMembers('Name')}
            >
              Sort by Name {sortField === 'Name' && (sortDirection === 'asc' ? '↑' : '↓')}
            </Button>
            <Button
              variant={sortField === 'Flight arrival time' ? 'primary' : 'outline-primary'}
              onClick={() => sortMembers('Flight arrival time')}
            >
              Sort by Arrival {sortField === 'Flight arrival time' && (sortDirection === 'asc' ? '↑' : '↓')}
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {apiError && (
        <div className="alert alert-warning" role="alert">
          {apiError}
        </div>
      )}
      
      {getSortedMembers().length === 0 ? (
        <p>No members found.</p>
      ) : (
        <Row>
          {getSortedMembers().map(member => (
            <Col md={6} key={member.id} className="mb-4">
              <div 
                style={{ 
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  padding: '15px',
                  height: '100%',
                  backgroundColor: 'white',
                }}
                className="hover-shadow"
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: canEditRecord(member.Email) ? '10px' : '0'
                }}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    backgroundColor: '#eee', 
                    marginRight: '20px',
                    borderRadius: '4px',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}>
                    {member.photoURL ? (
                      <img 
                        src={member.photoURL} 
                        alt={member.Name} 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <img 
                        src="/images/Grizzly_sprite.png"
                        alt="Default Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '0px'
                        }}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1.2rem', marginBottom: '8px' }}>{member.Name}</h3>
                    <p style={{ marginBottom: '4px' }}>
                      Arrival Time: {member['Flight arrival time'] || member.flightArrivalTime || member['Flight Arrival Time'] || 'N/A'}
                    </p>
                    <p style={{ color: '#666', margin: 0, display: 'flex', alignItems: 'center' }}>
                      Flight: {member['Flight # incoming'] || 'N/A'}
                      {member['Flight # incoming'] && getStatusBadge(member['Flight # incoming'])}
                    </p>
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end',
                  gap: '10px',
                  marginTop: '10px'
                }}>
                  <Link 
                    to={`/members/${member.id}`}
                    className="btn btn-outline-primary btn-sm"
                  >
                    View Details
                  </Link>
                  {canEditRecord(member.Email) && (
                    <Link 
                      to={`/members/${member.id}/edit`}
                      className="btn btn-primary btn-sm"
                    >
                      {isSuperUser && auth.currentUser?.email !== member.Email ? 'Edit as Admin' : 'Edit My Info'}
                    </Link>
                  )}
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      <style>
        {`
          .hover-shadow:hover {
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          }
        `}
      </style>
    </Container>
  );
}

export default Members; 